/** Per-member logical counters merged on every synced write (05-sync.md). */
export type VectorClock = Record<string, number>;

export const syncedTableNames = [
  'blocks',
  'block_properties',
  'block_acl',
  'database_schemas',
  'databases',
  'relations',
  'file_objects',
  'members',
] as const;

export type SyncedTableName = (typeof syncedTableNames)[number];

export const changeOperations = ['insert', 'update', 'delete'] as const;
export type ChangeOperation = (typeof changeOperations)[number];

/** Fields present on every mutable synced row used for LWW resolution. */
export type LwwComparableRow = {
  updatedAt: Date | string;
  syncVersion: number;
  updatedBy: string;
};

/** One row mutation queued on the client for push (05-sync.md). */
export type ClientChange = {
  table: SyncedTableName;
  operation: ChangeOperation;
  row: Record<string, unknown>;
  /** Server row version the client based this change on; null on insert. */
  baseSyncVersion: number | null;
};

export type PushResultStatus = 'applied' | 'conflict' | 'forbidden';

/** Per-row outcome after the server processes a client push. */
export type PushResult = {
  rowId: string;
  status: PushResultStatus;
  syncVersion?: number;
};

/** One ACL-filtered change delivered on pull or over WebSocket. */
export type SyncDelta = {
  seq: number;
  table: SyncedTableName;
  rowId: string;
  operation: ChangeOperation;
  changedBy: string;
  changedAt: string;
  payload: Record<string, unknown> | null;
};

export type SyncCatchupResponse = {
  seq: number;
  hasMore: boolean;
  changes: SyncDelta[];
};

export type SyncBootstrapSnapshot = {
  seq: number;
  tables: Record<SyncedTableName, Record<string, unknown>[]>;
};

export type SyncPushRequest = {
  changes: ClientChange[];
};

export type SyncPushResponse = {
  seq: number;
  results: PushResult[];
};

/** WebSocket frames (05-sync.md wire protocol). */
export type SyncSubscribeMessage = {
  type: 'subscribe';
  since: number;
};

export type SyncPushMessage = {
  type: 'push';
  changes: ClientChange[];
};

export type SyncPongMessage = {
  type: 'pong';
};

export type SyncClientMessage = SyncSubscribeMessage | SyncPushMessage | SyncPongMessage;

export type SyncDeltaMessage = {
  type: 'delta';
  seq: number;
  changes: SyncDelta[];
};

export type SyncAckMessage = {
  type: 'ack';
  seq: number;
  results: PushResult[];
};

export type SyncPingMessage = {
  type: 'ping';
};

export type SyncServerMessage = SyncDeltaMessage | SyncAckMessage | SyncPingMessage;

export type SyncMessage = SyncClientMessage | SyncServerMessage;

export type LwwWinner = 'server' | 'client';

export type LwwResolution = {
  winner: LwwWinner;
  /** Row state that lost the comparison (for block_versions snapshot). */
  loser: LwwWinner;
};
